import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import SelectField from "../../components/ui/SelectField";
import ColumnDataPreview from "../components/ColumnDataPreview";
import { Hash, User, BadgeDollarSign, Grid2X2Plus, ArrowRight } from "lucide-react";
import { renderFieldIcon } from "../../fields/FieldList";
import { useTranslation } from "react-i18next";

const PREVIEW_WIDTH = 320; // w-80, matches ColumnDataPreview

const GRID_COLS_MAP: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

const RuleFieldMapper = ({
  sourceOptions = {},
  sourcePlaceholder: _sourcePlaceholder = "Hubspot field",
  readonly = false,
  showDealMapping = true,
  showContactMapping = false,
  showCompanyMapping = false,
  showHeaderLabel = true,
  fieldMappings = {},
  supportedDataTypes = ["integer", "string", "double", "datetime", "bool", "boolean", "percent"],
  unSupportedFields = ["id", "start", "finish", "openedAt"],
  onChangeMappings,
  csvRows = [],
}) => {
  const { t } = useTranslation();
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const showMappingSize = [showDealMapping, showCompanyMapping, showContactMapping];

  // --- State & refs ---
  const [mappings, setMappings] = useState(fieldMappings);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const isInitialMount = useRef(true);
  const prevFieldMappingsRef = useRef(fieldMappings);

  // --- Mapping helpers ---
  // Normalize: lowercase + remove spaces/underscores/hyphens for flexible matching
  const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");

  const getSourceField = (
    destinationFieldId: string,
    formType: string,
    destinationFieldLabel?: string
  ) => {
    const destIdNorm = normalize(destinationFieldId);
    const destLabelNorm = destinationFieldLabel ? normalize(destinationFieldLabel) : null;

    const sourceField =
      sourceOptions[formType]?.find((src: any) => {
        const srcNorm = normalize(src.label || "");
        // Match against normalized field.id OR normalized field.label
        return srcNorm === destIdNorm || (destLabelNorm && srcNorm === destLabelNorm);
      }) || {};
    return sourceField;
  };

  const handleOnChangeSourceMapping = (formType, sourceField, mappingIndex) => {
    const typeMappings = (mappings && mappings[formType]) || [];
    const updatedTypeMappings = typeMappings.map((m, idx) =>
      idx === mappingIndex ? { ...m, source: sourceField } : m
    );
    const newMappings = {
      ...mappings,
      [formType]: updatedTypeMappings,
    };
    setMappings(newMappings);
    if (onChangeMappings) onChangeMappings(newMappings, formType, sourceField);
  };

  useEffect(() => {
    // Skip if fields are not loaded yet
    if (!fields || fields.size === 0) {
      return;
    }

    // Check if fieldMappings actually changed (deep comparison for initial mount)
    const fieldMappingsChanged =
      JSON.stringify(prevFieldMappingsRef.current) !== JSON.stringify(fieldMappings);

    if (!isInitialMount.current && !fieldMappingsChanged) {
      return;
    }

    const saleMappings = [];
    const contactMappings = [];
    const companyMappings = [];
    const supportedFields = fields
      .filter(
        (field) =>
          supportedDataTypes.includes(field.get("type")) &&
          !unSupportedFields.includes(field.get("id"))
      )
      .toJS();
    supportedFields.forEach((field) => {
      const mapping = {
        source: getSourceField(field.id, field.formType, field.label),
        type: field.type,
        destination: { value: field.id, label: field.label },
      };
      if (field.formType == "SALE") {
        const fieldMapping =
          fieldMappings["SALE"]?.find((item) => item.destination.value == field.id) || mapping;
        saleMappings.push(fieldMapping);
      } else if (field.formType == "CONTACT") {
        const fieldMapping =
          fieldMappings["CONTACT"]?.find((item) => item.destination.value == field.id) || mapping;
        contactMappings.push(fieldMapping);
      } else if (field.formType == "COMPANY") {
        const fieldMapping =
          fieldMappings["COMPANY"]?.find((item) => item.destination.value == field.id) || mapping;
        companyMappings.push(fieldMapping);
      }
    });

    const moveEmailToSecond = (arr: any[]) => {
      const idx = arr.findIndex((m) => (m.destination?.value ?? "").toLowerCase() === "email");
      if (idx <= 0) return arr;
      const out = arr.filter((_, i) => i !== idx);
      out.splice(1, 0, arr[idx]);
      return out;
    };

    const computed = {
      SALE: moveEmailToSecond(saleMappings),
      CONTACT: moveEmailToSecond(contactMappings),
      COMPANY: moveEmailToSecond(companyMappings),
    };

    // Only update if computed is different from current mappings
    const currentMappingsStr = JSON.stringify(mappings);
    const computedStr = JSON.stringify(computed);

    if (currentMappingsStr !== computedStr) {
      setMappings(computed);
      // Only call onChangeMappings on initial mount or when fieldMappings prop actually changed
      if (isInitialMount.current || fieldMappingsChanged) {
        if (onChangeMappings) onChangeMappings(computed);
      }
    }

    isInitialMount.current = false;
    prevFieldMappingsRef.current = fieldMappings;
  }, [fieldMappings, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Preview (hover + position) ---
  const getColumnData = (columnName: string): string[] => {
    if (!csvRows || csvRows.length === 0 || !columnName) return [];
    return csvRows.map((row) => row[columnName] || "").filter((val) => val !== "");
  };

  const handleColumnHover = (
    columnName: string | null,
    fieldId?: string | null,
    event?: React.MouseEvent
  ) => {
    if (!columnName) {
      setHoveredColumn(null);
      setHoveredFieldId(null);
      setPreviewPosition(null);
      return;
    }
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      setPreviewPosition({
        top: rect.bottom + 8,
        left: rect.right - PREVIEW_WIDTH,
      });
    }
    setHoveredColumn(columnName);
    setHoveredFieldId(fieldId || null);
  };

  // --- Render: mapping table ---
  const renderMappings = (headerLabel, formType) => {
    const typeMappings = mappings ? mappings[formType] || [] : [];

    return (
      <div className="space-y-4">
        {showHeaderLabel && (
          <div className="text-lg font-semibold text-gray-900 mb-4">{headerLabel}</div>
        )}
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4 bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="text-sm font-medium text-gray-700">{t("File column")}</div>
            <div className="w-6"></div>
            <div className="text-sm font-medium text-gray-700">{t("Attributes")}</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-gray-200">
            {typeMappings.map((mapping, index) => {
              const source = mapping.source && mapping.source.value ? mapping.source : undefined;
              const sourceColumnName = source?.value || null;
              const destinationFieldId = mapping.destination?.value || null;
              return (
                <div
                  key={`mapping${index}`}
                  className="grid grid-cols-[1fr_auto_1fr] gap-4 px-4 py-1 hover:bg-gray-50 transition-colors"
                  onMouseEnter={(e) => {
                    if (sourceColumnName)
                      handleColumnHover(sourceColumnName, destinationFieldId, e);
                  }}
                  onMouseLeave={() => handleColumnHover(null)}
                >
                  {renderFieldMapping(formType, mapping, index)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // --- Field icon ---
  const getFieldIcon = (field: any) => {
    if (!field) return <Grid2X2Plus className="w-4 h-4 text-gray-400" />;

    // Use renderFieldIcon from FieldList if available
    try {
      const iconElement = renderFieldIcon(field, "w-4 h-4 text-gray-500");
      if (iconElement) return iconElement;
    } catch {
      // Fallback if renderFieldIcon fails
    }

    // Fallback icons based on field type
    if (field.id === "id" || field.type === "integer" || field.type === "double") {
      return <Hash className="w-4 h-4 text-gray-500" />;
    }
    if (field.id === "assignees" || field.type === "contact") {
      return <User className="w-4 h-4 text-gray-500" />;
    }
    if (field.id === "estimatedRevenue" || field.id === "price") {
      return <BadgeDollarSign className="w-4 h-4 text-gray-500" />;
    }

    return <Grid2X2Plus className="w-4 h-4 text-gray-400" />;
  };

  // --- Render: single row (left col, arrow, select) ---
  const compactSelectStylesOverride = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "28px",
      height: "28px",
      borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
      borderRadius: "0.5rem",
      backgroundColor: state.isDisabled ? "#fbf9fa" : "#ffffff",
      fontSize: "13px",
      "&:hover": { borderColor: state.isFocused ? "#2563eb" : "#d1d5db" },
    }),
    valueContainer: (provided: any) => ({ ...provided, padding: "0 6px", height: "28px" }),
    input: (provided: any) => ({ ...provided, margin: 0, padding: 0 }),
    singleValue: (provided: any) => ({ ...provided, lineHeight: "26px", margin: 0 }),
    placeholder: (provided: any) => ({ ...provided, lineHeight: "26px", margin: 0 }),
    indicatorsContainer: (provided: any) => ({ ...provided, height: "26px" }),
    dropdownIndicator: (provided: any) => ({ ...provided, padding: "0 6px" }),
  };

  const renderFieldMapping = (formType, mapping, mappingIndex) => {
    const destination = mapping.destination;
    const source = mapping.source && mapping.source.value ? mapping.source : undefined;

    // Get field object for icon
    const fieldObj = fields?.find((f: any) => f.get("id") === destination.value);
    const fieldForIcon = fieldObj ? fieldObj.toJS() : { id: destination.value, type: mapping.type };

    return (
      <>
        {/* File Column (Left) - Show destination field with icon */}
        <div className="flex items-center gap-2 text-sm text-gray-900">
          {getFieldIcon(fieldForIcon)}
          <span className="font-medium">{destination.label}</span>
        </div>

        {/* Arrow */}
        <div className="flex items-center justify-center">
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </div>

        {/* Attributes (Right) - Select to choose source column from CSV */}
        <div className="relative flex items-center gap-2">
          <div className="flex-1">
            <SelectField
              menuPlacement="auto"
              menuPosition="fixed"
              placeholder={t("Select attribute")}
              value={source || null}
              isDisabled={readonly}
              isClearable={!readonly}
              options={
                sourceOptions[formType]?.map((opt: any) => ({
                  ...opt,
                  label: opt.label,
                })) ?? []
              }
              onChange={(sourceField) => {
                const value = sourceField == null ? {} : sourceField;
                handleOnChangeSourceMapping(formType, value, mappingIndex);
                setHoveredColumn(null);
                setPreviewPosition(null);
              }}
              stylesOverride={compactSelectStylesOverride}
            />
          </div>
        </div>
      </>
    );
  };

  const colNum = showMappingSize.filter(Boolean).length;
  const gridClass = GRID_COLS_MAP[colNum] ?? "grid-cols-1";
  const previewData = hoveredColumn ? getColumnData(hoveredColumn) : [];

  return (
    <div className={`grid ${gridClass} gap-4 overflow-y-auto relative`}>
      {showDealMapping && renderMappings("Deal", "SALE")}
      {showContactMapping && renderMappings("Contact", "CONTACT")}
      {showCompanyMapping && renderMappings("Account", "COMPANY")}

      {hoveredColumn && previewPosition && (
        <div
          className="fixed z-50"
          style={{
            top: `${previewPosition.top}px`,
            left: `${previewPosition.left}px`,
          }}
          onMouseLeave={() => handleColumnHover(null)}
        >
          <ColumnDataPreview
            columnName={hoveredColumn}
            data={previewData}
            fieldId={hoveredFieldId}
          />
        </div>
      )}
    </div>
  );
};

export default RuleFieldMapper;
